/**
 * Plain-text rendering of a ticket's conversation/notes entries for LLM context
 * (AI Overview and ticket quality review). Shared by the client and extension.
 */

export type TicketThreadEntry = {
  noteId?: string;
  conversationId?: string;
  content?: unknown;
  time?: unknown;
  user?: { name?: string; email?: string } | null;
  type?: unknown;
  publish?: unknown;
};

export type TicketThreadEntryLimits = {
  maxEntries: number;
  maxEntryChars: number;
};

export function stripTicketHtml(value: unknown): string {
  const text = value == null ? "" : String(value);
  if (!text.trim()) {
    return "";
  }
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h\d)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .trim();
}

export function formatTicketThreadEntries(
  label: string,
  entries: TicketThreadEntry[] | null | undefined,
  { maxEntries, maxEntryChars }: TicketThreadEntryLimits
): string[] {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }
  const lines: string[] = [`${label}:`];
  entries.slice(0, maxEntries).forEach((entry, index) => {
    const id =
      (entry.noteId != null && String(entry.noteId)) ||
      (entry.conversationId != null && String(entry.conversationId)) ||
      String(index + 1);
    const user = entry.user || {};
    const author = [user.name, user.email].filter(Boolean).join(" — ") || "Unknown author";
    const time = entry.time != null && String(entry.time).trim() ? String(entry.time).trim() : "unknown time";
    const type = entry.type != null && String(entry.type).trim() ? ` [${String(entry.type).trim()}]` : "";
    let content = stripTicketHtml(entry.content);
    if (content.length > maxEntryChars) {
      content = `${content.slice(0, maxEntryChars)}\n[...truncated...]`;
    }
    lines.push(`--- entry ${id}${type} by ${author} at ${time} ---`);
    lines.push(content || "(empty)");
  });
  if (entries.length > maxEntries) {
    lines.push(`[... ${entries.length - maxEntries} more entries omitted ...]`);
  }
  return lines;
}

/** Limits for the AI Overview: enough history to summarize, small enough to stay under Cortex's context cap. */
export const AI_OVERVIEW_ENTRY_LIMITS: TicketThreadEntryLimits = { maxEntries: 30, maxEntryChars: 1500 };

/** Customer conversation + notes sections appended to the AI Overview ticket context. */
export function formatAiOverviewThreadSections(
  conversations: TicketThreadEntry[] | null | undefined,
  notes: TicketThreadEntry[] | null | undefined
): string {
  const parts: string[] = [];
  const convo = formatTicketThreadEntries("Customer conversation", conversations, AI_OVERVIEW_ENTRY_LIMITS);
  if (convo.length) {
    parts.push(...convo, "");
  }
  parts.push(...formatTicketThreadEntries("Notes (internal, agent, and customer-facing)", notes, AI_OVERVIEW_ENTRY_LIMITS));
  return parts.join("\n").trim();
}
